import React, { useEffect } from 'react';
import { getIn, useFormikContext } from 'formik';
import { ProductVariantInput } from '@shopify-multiseller/graphql/partner/types';
import { Button } from '../primitives';
import { FormInput } from '../form';
import { Checkbox } from '../primitives';

export interface PricingInventoryFieldsProps {
  /** Formik path of the variant being edited, e.g. `variants.0` or `variant`. */
  namePrefix: string;
  isDigital: boolean;
  /** The stored (server-side) quantity for existing variants; 0 for new ones. */
  currentQuantity: number;
  disabled?: boolean;
  /** Notified when the digital "Unlimited quantity" toggle changes tracking. */
  onTrackQuantityChange?: (_tracked: boolean) => void;
  priceTestId?: string;
  quantityTestId?: string;
  /** Show the "Current quantity: N" hint under the input (variant editor). */
  showCurrentQuantityHint?: boolean;
}

type FormVariant = ProductVariantInput & { showSku?: boolean; isNew?: boolean };

/**
 * The shared price / quantity / SKU field-set for a single product variant.
 *
 * Used by both the product editor's pricing section (path `variants.{i}`) and
 * the full-screen variant editor (path `variant`). It owns the app's inventory
 * semantics in one place:
 * - `trackQuantity: true` means inventory is tracked (limited); the digital
 *   "Unlimited quantity" checkbox is therefore checked when tracking is OFF.
 * - The quantity input always displays the resulting stock. For existing
 *   variants edits are stored as `quantityAdjustment` (delta from the stored
 *   quantity); for new variants they are stored as `initialQuantity`.
 * - An effect clamps the adjustment so resulting stock can never go below 0.
 * - Adding a SKU reveals the SKU field; a non-empty SKU keeps it revealed.
 */
export const PricingInventoryFields: React.FC<PricingInventoryFieldsProps> = ({
  namePrefix,
  isDigital,
  currentQuantity,
  disabled,
  onTrackQuantityChange,
  priceTestId,
  quantityTestId,
  showCurrentQuantityHint,
}) => {
  const { values, setFieldValue } = useFormikContext<unknown>();
  const variant: FormVariant | undefined = getIn(values, namePrefix);

  const isNew = !!variant?.isNew;
  const trackQuantity = !!variant?.trackQuantity;
  const adjustment = typeof variant?.quantityAdjustment === 'number' ? variant.quantityAdjustment : undefined;
  const showSku = !!variant?.showSku || !!variant?.sku?.trim();
  const showQuantityInput = !isDigital || trackQuantity;

  // Reveal the SKU field whenever a SKU value exists (mirrors the sync the
  // variant editor previously did locally).
  useEffect(() => {
    if (variant?.sku?.trim() && !variant?.showSku) {
      setFieldValue(`${namePrefix}.showSku`, true);
    }
  }, [variant?.sku, variant?.showSku]);

  // Clamp: an adjustment must not push the resulting stock below zero.
  useEffect(() => {
    if (!isNew && typeof adjustment === 'number' && currentQuantity + adjustment < 0) {
      setFieldValue(`${namePrefix}.quantityAdjustment`, -currentQuantity);
    }
  }, [adjustment, currentQuantity, isNew]);

  const getQuantityValue = (): number => {
    if (isNew) {
      return variant?.initialQuantity || 0;
    }
    return currentQuantity + (adjustment ?? 0);
  };

  const handleQuantityChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;

    if (isNew) {
      setFieldValue(`${namePrefix}.initialQuantity`, numValue);
    } else {
      setFieldValue(`${namePrefix}.quantityAdjustment`, numValue - currentQuantity);
    }
  };

  const quantityAdjustmentMessage =
    !isNew && typeof adjustment === 'number' && adjustment !== 0 ? (
      <p className="mt-2 text-sm text-muted-foreground">
        {adjustment > 0 ? 'Increasing' : 'Decreasing'} quantity by {Math.abs(adjustment)}
      </p>
    ) : null;

  // When disabled, only show the SKU field if there's a non-empty SKU.
  const showSkuField = showSku && !(disabled && !variant?.sku?.trim());
  const showAddSku = !disabled && !showSku;

  return (
    /*
      Three related fields in one row. They were previously stacked — a
      quarter-width Price, a full-bleed horizontal rule, a quarter-width
      Quantity, another full-bleed rule, then a half-width SKU — so the section
      was mostly empty space with two strips drawn across the middle of it,
      separating fields that belong together.
    */
    <div className="space-y-3">
      <div className="grid gap-4 sm:grid-cols-3">
        <FormInput
          money
          cents
          label="Price"
          name={`${namePrefix}.price`}
          prefix="$"
          disabled={disabled}
          autoComplete="off"
          data-testid={priceTestId}
        />

        {showQuantityInput && (
          <div>
            <FormInput
              type="number"
              label="Quantity"
              name={`${namePrefix}.quantity`}
              disabled={disabled}
              value={getQuantityValue()}
              onChange={(e) => handleQuantityChange(e.target.value)}
              autoComplete="off"
              min={0}
              step={1}
              data-testid={quantityTestId}
            />
            {showCurrentQuantityHint && !isNew && (
              <div className="mt-1 text-xs text-muted-foreground">
                Current quantity: {currentQuantity}
              </div>
            )}
            {quantityAdjustmentMessage}
          </div>
        )}

        {showSkuField && (
          <FormInput
            type="text"
            label="SKU"
            help="Stock keeping unit"
            name={`${namePrefix}.sku`}
            disabled={disabled}
            autoComplete="off"
          />
        )}
      </div>

      {isDigital && (
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${namePrefix}-unlimited`}
            checked={!trackQuantity}
            onCheckedChange={(checked) => {
              const tracked = checked !== true;
              setFieldValue(`${namePrefix}.trackQuantity`, tracked);
              onTrackQuantityChange?.(tracked);
            }}
            disabled={disabled}
          />
          <label
            htmlFor={`${namePrefix}-unlimited`}
            className="cursor-pointer select-none text-sm text-foreground"
          >
            Unlimited quantity
          </label>
        </div>
      )}

      {showAddSku && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setFieldValue(`${namePrefix}.showSku`, true)}
        >
          + Add SKU
        </Button>
      )}
    </div>
  );
};
